/**
 * Provider-agnostic payment widget. Reads a lightweight payment-config (which
 * provider is active) FIRST — so the Fortis widget mounts immediately and mints
 * its own single-use ticket with no extra intention round-trip. For Stripe it
 * creates the SetupIntent up front and mounts the embedded Payment Element. The
 * emitted token (Fortis ticket id / Stripe PaymentMethod id) flows into the
 * existing submit `ticketId` slot unchanged.
 */
import React, {useEffect, useState} from 'react';
import FortisPaymentWidget from './FortisPaymentWidget';
import StripePaymentWidget from './StripePaymentWidget';
import {CheckoutService} from '../services/checkoutService';

interface PaymentWidgetProps {
    onTokenReady: (token: string) => void;
    onError: (message: string) => void;
    disabled?: boolean;
    /** Override the intention source (BioMatch supplies its own endpoint). */
    createIntention?: () => Promise<{provider: 'fortis' | 'stripe'; clientToken: string; publishableKey?: string}>;
    containerId?: string;
}

function Spinner() {
    return (
        <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mld-primary" />
            <span className="ml-3 text-sm text-gray-500">Loading payment form...</span>
        </div>
    );
}

const PaymentWidget: React.FC<PaymentWidgetProps> = ({
    onTokenReady,
    onError,
    disabled,
    createIntention,
    containerId,
}) => {
    const intentionFn = createIntention ?? (() => CheckoutService.createIntention());
    const [provider, setProvider] = useState<'fortis' | 'stripe' | null>(null);
    const [publishableKey, setPublishableKey] = useState<string | undefined>();
    const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
    const [fortisScriptUrl, setFortisScriptUrl] = useState<string | undefined>();

    useEffect(() => {
        let cancelled = false;
        CheckoutService.getPaymentConfig()
            .then(async (cfg) => {
                if (cancelled) return;
                setProvider(cfg.provider);
                setPublishableKey(cfg.publishableKey);
                setFortisScriptUrl(cfg.fortisScriptUrl);
                // Stripe needs a SetupIntent up front to mount the Payment Element.
                // Fortis mints its own single-use ticket inside FortisPaymentWidget,
                // so we do NOT create an intention here for it.
                if (cfg.provider === 'stripe') {
                    const intention = await intentionFn();
                    if (!cancelled) setStripeClientSecret(intention.clientToken);
                }
            })
            .catch(() => {
                if (!cancelled) onError('Unable to initialize payment form. Please try again.');
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!provider) return <Spinner />;

    if (provider === 'stripe') {
        if (!publishableKey) {
            onError('Payment configuration error. Please contact support.');
            return null;
        }
        if (!stripeClientSecret) return <Spinner />;
        return (
            <StripePaymentWidget
                clientSecret={stripeClientSecret}
                publishableKey={publishableKey}
                onTokenReady={onTokenReady}
                onError={onError}
                disabled={disabled}
            />
        );
    }

    // Fortis: FortisPaymentWidget mints its own fresh single-use ticket per mount
    // via the intention source (shop default or the BioMatch endpoint).
    return (
        <FortisPaymentWidget
            onTicketReady={onTokenReady}
            onError={onError}
            disabled={disabled}
            createIntention={intentionFn}
            containerId={containerId}
            scriptUrl={fortisScriptUrl}
        />
    );
};

export default PaymentWidget;
